import type { SessionResponse } from "@src/api/client/auth/models"
import customInstance from "@src/api/instance"
import { atom, useAtomValue } from "jotai"
import { atomWithQuery } from "jotai-tanstack-query"
import { useCallback } from "react"

export type SessionUser = NonNullable<SessionResponse["user"]>

/**
 * セッション情報を取得するatom (jotai-tanstack-query)
 */
export const sessionQueryAtom = atomWithQuery(() => ({
  queryKey: ["get-session"],
  queryFn: () =>
    customInstance<SessionResponse>("/api/auth/get-session", {
      method: "GET",
    }),
}))

/**
 * セッションユーザー情報を取得する派生atom
 */
export const sessionUserAtom = atom<SessionUser | null>((get) => {
  const query = get(sessionQueryAtom)
  return query.data?.user ?? null
})

/**
 * セッション読み込み中かどうかを取得する派生atom
 */
export const sessionLoadingAtom = atom((get) => {
  const query = get(sessionQueryAtom)
  return query.isPending
})

/**
 * セッションエラーを取得する派生atom
 */
export const sessionErrorAtom = atom<Error | null>((get) => {
  const query = get(sessionQueryAtom)
  return query.error ?? null
})

/**
 * セッション情報が有効かどうかを取得する派生atom
 */
export const isSessionValidAtom = atom((get) => {
  const query = get(sessionQueryAtom)
  return !query.isPending && !query.error && query.data?.user != null
})

/**
 * プロフィールが完了しているかどうかを判定する派生atom
 * LINEログインの場合、電話番号と住所が未入力の場合は未完了とみなす
 */
export const isProfileCompleteAtom = atom((get) => {
  const user = get(sessionUserAtom)
  if (!user) return false

  // 必須フィールドが入力されているかチェック
  const phoneNumber = user.phoneNumber as string | undefined
  const address = user.address as string | undefined

  return Boolean(phoneNumber && phoneNumber.trim() !== "" && address && address.trim() !== "")
})

/**
 * Echoes連携オーナーかどうかを判定する派生atom (E-08)
 * echoesOwnerId が設定済みのオーナーは Echoes 側でデータ管理されるため、
 * AgentCall 上での物件・部屋・賃借人の作成/編集/削除は許可しない。
 */
export const isEchoesOwnerAtom = atom((get) => {
  const user = get(sessionUserAtom)
  if (!user) return false

  const echoesOwnerId = user.echoesOwnerId as string | null | undefined
  return Boolean(echoesOwnerId && echoesOwnerId.trim() !== "")
})

/**
 * Echoes連携オーナーかどうかを取得するフック (E-08)
 */
export function useIsEchoesOwner() {
  return useAtomValue(isEchoesOwnerAtom)
}

/**
 * Echoes連携の二層同意がまだ完了していないかどうかを判定する派生atom
 * Echoesオーナーかつ echoesLinkedAt が未設定（=未同意）の場合に true
 */
export const isEchoesConsentRequiredAtom = atom((get) => {
  const user = get(sessionUserAtom)
  if (!user) return false
  const echoesOwnerId = user.echoesOwnerId as string | null | undefined
  if (!echoesOwnerId) return false
  // echoesLinkedAt はISO文字列（未同意ならnull/undefined）
  const echoesLinkedAt = user.echoesLinkedAt as string | null | undefined
  return !echoesLinkedAt
})

/**
 * セッション情報を再取得するフック
 */
export function useSessionRefetch() {
  const query = useAtomValue(sessionQueryAtom)
  return useCallback(() => query.refetch(), [query])
}
